import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Ladybug — a cheerful garden ladybird, storybook style: a glossy red dome of
// two wing-case halves scattered with charcoal dots, over a soft charcoal body
// with a big friendly face and two short bobble antennae. Faces +Z, stands on
// six tiny stub legs at y = 0. About 0.2 m tall.
//
// Rig layout (flat — each named node moves about its own pivot):
//   - "body"    : the charcoal body + head + face + antennae. Breathes in idle.
//   - "shell-l" / "shell-r" : the two red wing-case halves (dots ride along).
//     They hinge at the front so the back flicks up — the signature move.
// The six stub legs are static (unnamed) so the feet stay on the ground.

const RED = "#e0524a";        // glossy ladybird red
const RED_DARK = "#b83f3a";   // shaded shell rim
const CHARCOAL = "#2c2f36";   // body, dots, legs (never pure black)
const EYE_WHITE = "#f7f3e8";  // eye whites (never pure white)
const PUPIL = "#332b33";      // dark plum pupils
const CREAM = "#f6efe3";      // eye highlights + the little face patch

// The pivots the animations move the named nodes about. The shells hinge just
// behind the head, up where the wing cases meet the collar.
const BODY_PIVOT: [number, number, number] = [0, 0, 0];             // ground contact
const SHELL_L_PIVOT: [number, number, number] = [-0.02, 0.12, 0.05];
const SHELL_R_PIVOT: [number, number, number] = [0.02, 0.12, 0.05];

// One big friendly eye: white ball, dark pupil slightly forward, tiny highlight.
function eye(x: number): Part[] {
  return [
    { geometry: new THREE.SphereGeometry(0.024, 10, 8), color: EYE_WHITE,
      position: [x, 0.105, 0.135], name: "body" },
    { geometry: new THREE.SphereGeometry(0.013, 8, 6), color: PUPIL,
      position: [x, 0.105, 0.152], name: "body" },
    { geometry: new THREE.SphereGeometry(0.005, 6, 5), color: CREAM,
      position: [x + 0.006, 0.112, 0.161], name: "body" },
  ];
}

// One short antenna: a thin stalk with a friendly bobble tip.
function antenna(side: 1 | -1): Part[] {
  return [
    { geometry: new THREE.CylinderGeometry(0.005, 0.007, 0.055, 6), color: CHARCOAL,
      position: [side * 0.028, 0.165, 0.125], rotation: [0.45, 0, -side * 0.4], name: "body" },
    { geometry: new THREE.SphereGeometry(0.012, 8, 6), color: CHARCOAL,
      position: [side * 0.040, 0.190, 0.138], name: "body" },
  ];
}

// One wing-case half: a red half-dome (a full sphere squashed to an ellipsoid
// so its underside stays closed when it flicks open) plus its charcoal dots,
// all sharing the half's name so they hinge together.
function shellHalf(side: 1 | -1): Part[] {
  const name = side > 0 ? "shell-r" : "shell-l";
  const dot = (x: number, y: number, z: number): Part => ({
    geometry: new THREE.SphereGeometry(0.016, 8, 6), color: CHARCOAL,
    position: [side * x, y, z], scale: [1, 0.5, 1], name,
  });
  return [
    // The half-dome itself — the two halves overlap at the middle so the
    // shell reads as one dome with a shallow seam, not two lobes.
    { geometry: new THREE.SphereGeometry(0.10, 14, 12), color: RED,
      position: [side * 0.033, 0.082, -0.02], scale: [0.72, 0.82, 1.05], name },
    // A darker rim along the outer lower edge for shading.
    { geometry: new THREE.SphereGeometry(0.095, 12, 10), color: RED_DARK,
      position: [side * 0.036, 0.064, -0.02], scale: [0.74, 0.55, 1.06], name },
    // Three charcoal dots per half, sitting proud of the dome.
    dot(0.052, 0.150, 0.035),
    dot(0.080, 0.138, -0.045),
    dot(0.042, 0.140, -0.095),
  ];
}

// One tiny stub leg reaching from the belly to the ground.
function leg(side: 1 | -1, z: number): Part {
  return {
    geometry: new THREE.CylinderGeometry(0.009, 0.011, 0.045, 6), color: CHARCOAL,
    position: [side * 0.055, 0.021, z], rotation: [0, 0, -side * 0.4],
  };
}

export const ladybug = defineModel({
  id: "ladybug",
  batchId: "mess",
  name: "Ladybug",
  tags: ["mess", "cute", "animated", "creature", "insect", "ladybug", "beetle", "bug", "garden", "tiny"],
  summary: "A cheerful red-domed ladybug with charcoal dots and a big friendly face.",
  description:
    "A storybook ladybird: a glossy red dome of two dotted wing-case halves " +
    "over a soft charcoal body, a big friendly face with bead-bright eyes and " +
    "two short bobble antennae, standing on six tiny stub legs. Breathes at " +
    "rest (idle) and flicks its wing cases open at the back (flick).",
  author: "models",
  footprint: 0.16,
  tiles: { w: 1, h: 1 },
  parts(): Part[] {
    return [
      // ── body + head (named, breathes) ────────────────────────────────────
      // The soft charcoal body the shell sits over.
      { geometry: new THREE.SphereGeometry(0.082, 12, 10), color: CHARCOAL,
        position: [0, 0.075, -0.005], scale: [0.9, 0.85, 1.05], name: "body" },
      // The round head peeking out the front.
      { geometry: new THREE.SphereGeometry(0.058, 12, 10), color: CHARCOAL,
        position: [0, 0.095, 0.105], name: "body" },
      // A little cream face patch behind the eyes.
      { geometry: new THREE.SphereGeometry(0.040, 10, 8), color: CREAM,
        position: [0, 0.090, 0.128], scale: [1.15, 0.85, 0.55], name: "body" },
      ...eye(0.028), ...eye(-0.028),
      ...antenna(1), ...antenna(-1),

      // ── wing cases (named, flick) ────────────────────────────────────────
      ...shellHalf(1), ...shellHalf(-1),

      // ── legs (static) ────────────────────────────────────────────────────
      leg(1, 0.055), leg(1, 0), leg(1, -0.055),
      leg(-1, 0.055), leg(-1, 0), leg(-1, -0.055),
    ];
  },
  animations: [
    // Idle — a soft breath: the body swells gently while the antennae-carrying
    // head mass bobs a whisker. Calm and content.
    {
      id: "idle",
      label: "Idle breathing",
      duration: 2.2,
      loop: true,
      tracks: [
        {
          target: "body", pivot: BODY_PIVOT,
          scale: [
            { t: 0, value: [1, 1, 1], ease: "sine" },
            { t: 1.1, value: [1.02, 1.05, 1.02], ease: "sine" },
            { t: 2.2, value: [1, 1, 1], ease: "sine" },
          ],
        },
        {
          target: "shell-l", pivot: SHELL_L_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.1, value: [0, 0.005, 0], ease: "sine" },
            { t: 2.2, value: [0, 0, 0], ease: "sine" },
          ],
        },
        {
          target: "shell-r", pivot: SHELL_R_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0], ease: "sine" },
            { t: 1.1, value: [0, 0.005, 0], ease: "sine" },
            { t: 2.2, value: [0, 0, 0], ease: "sine" },
          ],
        },
      ],
    },

    // Flick — the signature move: both wing cases hinge up at the back (each
    // rolling slightly out to its own side), hold a beat, and settle shut.
    {
      id: "flick",
      label: "Wing-case flick",
      duration: 2.6,
      loop: true,
      tracks: [
        {
          target: "shell-l", pivot: SHELL_L_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeOut" },
            { t: 0.35, value: [0.5, 0, 0.3], ease: "easeOut" },
            { t: 0.7, value: [0.42, 0, 0.25], ease: "sine" },
            { t: 1.05, value: [0.5, 0, 0.3], ease: "sine" },
            { t: 1.5, value: [0, 0, 0], ease: "bounce" },
            { t: 2.6, value: [0, 0, 0] },
          ],
        },
        {
          target: "shell-r", pivot: SHELL_R_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0], ease: "easeOut" },
            { t: 0.35, value: [0.5, 0, -0.3], ease: "easeOut" },
            { t: 0.7, value: [0.42, 0, -0.25], ease: "sine" },
            { t: 1.05, value: [0.5, 0, -0.3], ease: "sine" },
            { t: 1.5, value: [0, 0, 0], ease: "bounce" },
            { t: 2.6, value: [0, 0, 0] },
          ],
        },
        // The body gives a happy little dip as the cases snap shut.
        {
          target: "body", pivot: BODY_PIVOT,
          scale: [
            { t: 0, value: [1, 1, 1], ease: "sine" },
            { t: 1.4, value: [1, 1, 1], ease: "sine" },
            { t: 1.6, value: [1.03, 0.95, 1.03], ease: "sine" },
            { t: 1.9, value: [1, 1, 1], ease: "sine" },
            { t: 2.6, value: [1, 1, 1] },
          ],
        },
      ],
    },
  ],
});
